import equal from 'fast-deep-equal'

import { Arrow } from '../arrow/arrow.tool'
import Base from '../paperTool/PaperTool'
import { Coordinate } from '../../../../../../models/activeDrawable'
import { ACTIVE_OPACITY_VALUE, JOIST_COLOR, LOCKED_OPACITY_VALUE } from '../../../../../../shared/constants/colors'
import { DRAWABLE_TYPES, DRAWING_TYPES, PAPER_ELEMENT_TYPE } from '../../../../../../shared/constants/drawable-types'
import { convertScaleStringToEnum } from '../../../../../../utils/calculations/scaleConversion/scaleConversion'
import { isNonAreaJoistLine } from '../../../../../../utils/project/project-helper-functions'
import { IMUP2DDrawableCutout, PaperToolConfig, REGION_ENUMS } from '../../../../../types'

/**
 * Workspace.tool.tsx
 * High level Paper.js workspace management functions that modify the Paper.project state
 */
export class Workspace extends Base {
    static NAME = 'WORKSPACE'

    // Cutout processing can reorder a shape's coordinates without changing its shape, so compare
    // order-insensitively. Static so other code (e.g. the materials nav tree) can reuse this exact
    // comparison via `Workspace.compareCoordinatesIgnoringOrder`.
    static compareCoordinatesIgnoringOrder = (basisCoord?: Coordinate[], comparatorCoord?: Coordinate[]): boolean => {
        if (!basisCoord || !comparatorCoord) return basisCoord === comparatorCoord

        if (basisCoord.length !== comparatorCoord.length) return false

        const toCoordString = (coord: Coordinate) => `${coord[0].toFixed(1)}-${coord[1].toFixed(1)}`
        const basisCoordStrings = basisCoord.map(toCoordString).sort()
        const comparatorCoordStrings = comparatorCoord.map(toCoordString).sort()

        return equal(basisCoordStrings, comparatorCoordStrings)
    }

    public defaultLabelBGColor: paper.Color
    public doubleJoistLabelBGColor: paper.Color
    public singleJoistLabelTextColor: paper.Color
    public doubleJoistLabelTextColor: paper.Color

    constructor(config: PaperToolConfig) {
        super(config)
        this.name = Workspace.NAME

        this.defaultLabelBGColor = new this.paper.Color(JOIST_COLOR.WHITE)
        this.doubleJoistLabelBGColor = new this.paper.Color(JOIST_COLOR.BLUE)
        this.singleJoistLabelTextColor = new this.paper.Color(JOIST_COLOR.BLACK)
        this.doubleJoistLabelTextColor = new this.paper.Color(JOIST_COLOR.WHITE)
    }

    clear = (): void => {
        const viewElement = this.paper.view.element

        this.paper.project.remove()
        this.paper.project = new this.paper.Project(viewElement)
    }

    undo = (): this => {
        this.paper.project.activeLayer.lastChild.remove()

        return this
    }

    getItemWithPaperId = <T extends paper.Item>(id: number): T | null => {
        const items = this.paper.project.getItems({
            id,
        })

        return items.length === 1 ? (items[0] as T) : null
    }

    getItemsWithPaperIds = <T extends paper.Item>(ids: number[]): T[] | null => {
        return (
            (this.paper.project.getItems({
                match: (item: paper.Item) => ids.includes(item.id),
            }) as T[]) || null
        )
    }

    removeItemWithPaperId = (id: number): boolean => {
        return (
            this.paper.project
                .getItem({
                    id,
                })
                ?.remove() ?? false
        )
    }

    removeItemsWithOpeningGroupId = (id: number): void => {
        return this.paper.project
            .getItems({
                data: {
                    opening_group_id: id,
                },
            })
            .forEach((item) => item.remove())
    }

    removeItemsWithDrawableId = (id: number): void => {
        return this.paper.project
            .getItems({
                data: {
                    drawable_id: id,
                },
            })
            .forEach((item) => item.remove())
    }

    addPaperItem = (item: paper.Item): number => {
        return this.paper.project.activeLayer.addChild(item).id
    }

    // Generates a temporary point for calculations in this PaperScope
    generatePoint = (coordinates: Coordinate): paper.Point => {
        return new this.paper.Point(coordinates[0], coordinates[1])
    }

    getItemWithDrawableId = (id: number): paper.Item | null => {
        const items = this.paper.project.getItems({
            data: {
                drawable_id: id,
            },
        })
        // If more than one item is found,
        // then select the element itself and not the
        // label. The Item does not have any children as it is
        // not a group. Applies to joists and headers

        if (items.length > 1) {
            const itemsWithoutChildren = items.find((item) => item.data.shapeType && !item.children)

            return itemsWithoutChildren ? itemsWithoutChildren : null
        } else if (items.length === 1) {
            return items[0]
        }

        return null
    }

    getItemWithOpeningLocationId = (id: number): paper.Item | null => {
        const items = this.paper.project.getItems({
            data: {
                opening_location_id: id,
            },
        })

        if (items.length) return items[0]

        return null
    }

    getItemsWithOpeningLocationId = (id: number): paper.Item[] | null => {
        const items = this.getItemsWithCriteria('data', (data) => data.opening_location_id === id)

        if (items.length) return items

        return null
    }

    getItemsWithDrawableId = (id: number): paper.Item[] => {
        const items = this.paper.project.getItems({
            data: {
                drawable_id: id,
            },
        })

        return items
    }

    /**
     * Get a paperItem from the region id
     * @param id the region id
     * @returns paper item or null
     */
    getItemWithRegionId = (id: number): paper.Item | null => {
        const items = this.paper.project.getItems({
            data: {
                region_id: id,
            },
        })
        // If more than one item is found,
        // then select the element itself and not the
        // label.
        // The Item does not have any children as it is
        // not a group.
        // Applies to joists and headers

        if (items.length > 1) {
            const itemsWithoutChildren = items.find((item) => item.data.shapeType && !item.children)

            return itemsWithoutChildren ? itemsWithoutChildren : null
        } else if (items.length === 1) {
            return items[0]
        }

        return null
    }

    /**
     *
     * @returns all region item by region_id
     */
    getRegionItemByRegionId = (region_id): paper.Item => {
        return this.paper.project.getItem({
            data: {
                region_id: region_id,
                shapeType: REGION_ENUMS.TYPE,
            },
        })
    }

    getAllRegionItems = (): paper.Item[] => {
        return this.paper.project.getItems({
            data: { shapeType: REGION_ENUMS.TYPE },
        })
    }

    getAllDrawableItems = (): paper.Item[] => {
        return this.paper.project.getItems({
            data: (data) => data?.drawable_id,
        })
    }

    getArrowElement = (): paper.Item => {
        return this.paper.project.getItem({
            data: (data) => data?.toolName === Arrow.NAME,
        })
    }

    getPlanRaster = (): paper.Raster | null => {
        const rasters = this.getItemsWithCriteria('data', (data) => data.isMainRaster === true)

        if (rasters.length === 1) {
            return rasters[0] as paper.Raster
        }

        return null
    }

    toggleDrawablesWithCondition = (visibilityTest: (item: paper.Item) => boolean): void => {
        this.paper.project
            .getItems({
                data: function (data) {
                    return data?.drawable_id || data?.aiSuggestion?.id
                },
            })
            .forEach((item: paper.Item) => {
                item.visible = visibilityTest(item)
            })
    }

    toggleJoistLinesVisibility = (openingGroupId: number, isVisible: boolean): void => {
        this.getItemsWithCriteria('data', (data) => {
            return data.opening_group_id === openingGroupId && data.shapeType === DRAWING_TYPES.SECTION
        }).forEach((item: paper.Item) => {
            item.visible = isVisible
        })
    }

    private getItemOpacity(item: paper.Item, shouldLock: boolean): number {
        if (shouldLock) {
            return LOCKED_OPACITY_VALUE
        }

        return item.data.originalOpacityValue ?? ACTIVE_OPACITY_VALUE
    }

    private setItemOpacity = (item: paper.Item, shouldLock: boolean) => {
        const itemOpacity = this.getItemOpacity(item, shouldLock)

        if ('aiSuggestion' in item?.data) {
            item.opacity = itemOpacity

            return
        }

        if (item.fillColor) {
            item.fillColor.alpha = itemOpacity
        }

        if (item.strokeColor) {
            item.strokeColor.alpha = itemOpacity
        }
    }

    private shouldItemBeVisible = (item: paper.Item, activeChunkId: number, isHighlightsVisible: boolean) => {
        if (item.data.type === PAPER_ELEMENT_TYPE.HIGHLIGHT && isHighlightsVisible) {
            return item.data.document_chunk_id === activeChunkId
        }

        return !isNonAreaJoistLine(item) && item.data.document_chunk_id === activeChunkId
    }

    lockItems = (
        shouldLock = true,
        openingIds?: number[],
        selectedAIMaterialType?: string,
        selectedSubType?: DRAWABLE_TYPES | null
    ): void => {
        const items = this.paper.project.getItems({
            data: (data) => {
                const aiTypeMatches =
                    data?.aiSuggestion?.type === selectedAIMaterialType &&
                    (selectedSubType == null || data?.aiSuggestion?.settings.beam_type === selectedSubType)
                const id = data?.drawable_id || data?.aiSuggestion?.id

                if (openingIds === undefined && 'aiSuggestion' in data && aiTypeMatches) {
                    return false
                }

                if (!data?.type) return false

                if (openingIds?.length) {
                    return !openingIds.includes(id)
                }

                return true
            },
        })

        items.forEach((item: paper.Item) => {
            item.locked = shouldLock
            this.setItemOpacity(item, shouldLock)
        })
    }

    toggleModificationsWithCondition = (visibilityTest: (item: paper.Item) => boolean): void => {
        this.paper.project
            .getItems({
                data: function (data) {
                    return data.materialModificationConflict
                },
            })
            .forEach((item: paper.Item) => {
                item.visible = visibilityTest(item)
            })
    }

    showAllDrawablesOnPage = (activeChunkId: number, isHighlightsVisible: boolean): void => {
        this.paper.project
            .getItems({
                data: (data) => data.drawable_id,
            })
            .forEach((item: paper.Item) => {
                // By default, when nothing is selected, everything except non area joists are shown
                item.visible = this.shouldItemBeVisible(item, activeChunkId, isHighlightsVisible)
                item.data.isActive = false
                item.selected = false
                if (item.fillColor) item.fillColor.alpha = item.data.originalOpacityValue
                if (item.strokeColor) item.strokeColor.alpha = item.data.originalOpacityValue
                if (item.data.type === PAPER_ELEMENT_TYPE.HIGHLIGHT && isHighlightsVisible) {
                    this.sendItemOnlyOnTopOfMainRaster(item)
                }
            })
    }

    deactivateAllDrawables = (): void => {
        this.paper.project
            .getItems({
                data: (data) => data?.drawable_id || data?.aiSuggestion?.id,
            })
            .forEach((item: paper.Item) => {
                if (isNonAreaJoistLine(item)) {
                    item.visible = false
                }

                item.data.isActive = false
                item.selected = false
                if (item.fillColor) item.fillColor.alpha = item.data.originalOpacityValue
                if (item.strokeColor) item.strokeColor.alpha = item.data.originalOpacityValue
                if (item.data.type === PAPER_ELEMENT_TYPE.HIGHLIGHT) this.sendItemOnlyOnTopOfMainRaster(item)
            })
    }

    activateTool = (name: string) => {
        this.mediator.mediate('tools', { activeTool: name })
    }

    setTooltip = ({ visible, title, color }: { visible: boolean; title: string; color: string }) => {
        this.mediator.mediate('common', {
            tooltip: { visible, title, color },
        })
    }

    setFlagTooltip = ({ visible, flagId }: { visible: boolean; flagId: number | null }) => {
        this.mediator.mediate('materialFlags', {
            flagTooltip: { visible, flagId },
        })
    }

    toggleDrawable = (activeDrawableId: number, exiting?: boolean) => {
        this.mediator.mediate('2D', { activeDrawableId, exiting })
    }

    updateScaleForRegion = (paperId: number, newScale: string) => {
        const path = this.getItemWithPaperId(paperId)

        if (path?.data.scale !== undefined) {
            // Keep the numeric scale enum in metadata; the display label is not parseable for measurements.
            path.data.scale = convertScaleStringToEnum(newScale)
            const labelItem = this.getItemWithPaperId<paper.TextItem>(path.data.labelId)

            if (labelItem) {
                labelItem.content = newScale
            }
        }
    }

    /**
     * Function returns stroke width based on Raster
     * Currently the 100 magic number comes from guess + check to ensure that aesthetics are consistent across plans with different resolutions
     * Plan resolution should be the primary variable in determining line width
     * @param raster
     * @returns
     */
    calculateStrokeWidthBasedOnRaster = (raster: paper.Raster): number => {
        return raster.width / 100
    }

    compareCoordinatesUsingAPIModels = (
        coordsAndCutouts: { coordinates: Coordinate[]; cutouts?: IMUP2DDrawableCutout[] },
        basisCoordinates: Coordinate[],
        basisCutouts: IMUP2DDrawableCutout[]
    ): boolean => {
        const { coordinates, cutouts } = coordsAndCutouts

        return (
            Workspace.compareCoordinatesIgnoringOrder(coordinates, basisCoordinates) &&
            equal(cutouts || [], basisCutouts)
        )
    }

    updateItemCoordinates = (
        item: paper.Item,
        newCoordinates: Coordinate[],
        newCutouts: IMUP2DDrawableCutout[],
        metadataUpdateFunction: (item: paper.Item) => void
    ): void => {
        const paperItem =
            item instanceof this.paper.Group && item.children && item.children.length > 0 ? item.children[0] : item
        const shapeType = item.data.shapeType

        if (paperItem instanceof this.paper.Path && shapeType !== DRAWING_TYPES.POINT && newCutouts.length === 0) {
            paperItem.segments = newCoordinates.map((coord) => new this.paper.Segment(coord))
        } else if (paperItem instanceof this.paper.CompoundPath) {
            const outsidePath = paperItem.children[0] as paper.Path

            outsidePath.segments = newCoordinates.map((coord) => new this.paper.Segment(coord))

            const existingCutouts = (paperItem.children as paper.Path[]).slice(1)

            existingCutouts.forEach((cutout) => cutout.remove())

            newCutouts.forEach((cutout) => {
                const cutoutPath = new this.paper.Path({
                    segments: cutout.coordinates.map((coord) => new this.paper.Segment(coord)),
                    closed: true,
                    insert: false,
                })

                if (cutoutPath.clockwise) cutoutPath.reverse()
                paperItem.addChild(cutoutPath)
            })

            metadataUpdateFunction(paperItem)
        } else if (shapeType === DRAWING_TYPES.POINT) {
            paperItem.position.x = newCoordinates[0][0]
            paperItem.position.y = newCoordinates[0][1]
        } else if (
            paperItem instanceof this.paper.Path &&
            shapeType !== DRAWING_TYPES.POINT &&
            newCutouts.length !== 0
        ) {
            paperItem.segments = newCoordinates.map((coord) => new this.paper.Segment(coord))
            const cutoutPaths = newCutouts.map((cutout) => {
                const p = new this.paper.Path({
                    segments: cutout.coordinates.map((coord) => new this.paper.Segment(coord)),
                    closed: true,
                    insert: false,
                })

                if (p.clockwise) p.reverse()

                return p
            })

            const path = new this.paper.CompoundPath({
                children: [paperItem, ...cutoutPaths],
            })

            path.data = { ...paperItem.data }

            this.styleNewCompoundPathShape(path, paperItem)

            metadataUpdateFunction(path)
        }
    }
}

export default Workspace
